// Browser-side doorway to the server. The GitHub token never reaches the
// client — every GraphQL document goes through POST /api/github/graphql.

export class UnauthenticatedError extends Error {
  constructor() {
    super('unauthenticated')
  }
}

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

/** GitHub's API budget is exhausted. `resetAt` (epoch ms) is when it frees up. */
export class RateLimitError extends ApiError {
  readonly resetAt?: number

  constructor(message: string, resetAt?: number) {
    super(message, 429)
    this.resetAt = resetAt
  }
}

function getCsrfToken(): string {
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=')
    if (eq > 0 && part.slice(0, eq).trim() === 'csrf-token') {
      return decodeURIComponent(part.slice(eq + 1).trim())
    }
  }
  return ''
}

function redirectToSignin(): void {
  if (window.location.pathname !== '/signin') {
    window.location.href = '/signin'
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    redirectToSignin()
    throw new UnauthenticatedError()
  }
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as {
      error?: string
      errors?: Array<{ message: string }>
      resetAt?: number
    } | null
    const message =
      json?.errors?.map((e) => e.message).join('; ') ?? json?.error ?? `request failed (${res.status})`
    if (res.status === 429) throw new RateLimitError(message, json?.resetAt)
    throw new ApiError(message, res.status)
  }
  return (await res.json()) as T
}

export async function get<T>(path: string): Promise<T> {
  return handleResponse<T>(await fetch(path))
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return handleResponse<T>(
    await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-csrf-token': getCsrfToken() },
      body: JSON.stringify(body),
    }),
  )
}

type GraphqlRequest = {
  query: string
  variables?: Record<string, unknown>
  preview?: 'issue_fields'
  touchedIds?: string[]
}

export async function graphql<T>(opts: GraphqlRequest): Promise<T> {
  const json = await post<{ data: T }>('/api/github/graphql', opts)
  return json.data
}

export async function graphqlWrite(opts: GraphqlRequest): Promise<{ universeBehind: boolean }> {
  const json = await post<{ universeBehind?: boolean }>('/api/github/graphql', opts)
  return { universeBehind: json.universeBehind === true }
}

/** UTF-8-safe base64 for the GraphQL FileChanges API (commitAppData contents). */
export function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin)
}

/** Reorder one group of a context's manual order. The server merges this group
 *  into the current file (read-modify-write under a lock), so concurrent
 *  reorders of different groups can't clobber. */
export async function reorderContextGroup(body: {
  context: string
  group: string
  orderedIds: string[]
}): Promise<{ oid?: string }> {
  return post('/api/orders/reorder', body)
}

/** Commit saved-search files to the app-data branch. The server serialises
 *  writes and retries on a stale head, so the client never touches expectedHeadOid. */
export async function commitAppData(body: {
  message: string
  additions?: Array<{ path: string; contents: string }>
  deletions?: Array<{ path: string }>
}): Promise<{ oid?: string }> {
  return post('/api/saved-searches/commit', body)
}

/** Record a QA verification: dispatches the work repo's verify-qa workflow so
 *  the bot applies `system/verified-qa` (the browser can't — the work repo's
 *  label guard strips hand-applied `system/*` labels). Async — the marker lands
 *  once the workflow runs. */
export async function dispatchVerifyQa(issue: number): Promise<{ ok: boolean }> {
  return post('/api/github/verify-qa', { issue })
}

export async function signOut(): Promise<void> {
  await fetch('/api/auth/signout', { method: 'POST', headers: { 'x-csrf-token': getCsrfToken() } })
  window.location.href = '/signin'
}

export type Me = {
  login: string
  name: string
  avatarUrl: string
  /** Member of the reorder team — may drag-reorder other people's lists. */
  canReorderOthers: boolean
  /** Member of the verifiers team — sees the QA/prod verify affordances. */
  isVerifier: boolean
  /** Member of the managers team — sees the Insights Individuals lens. */
  isManager: boolean
}

/** Returns null when signed out (the caller decides whether to redirect). */
export async function fetchMe(): Promise<Me | null> {
  const res = await fetch('/api/me')
  if (res.status === 401) return null
  if (!res.ok) throw new ApiError(`me failed (${res.status})`, res.status)
  return (await res.json()) as Me
}
